import { Controller } from "@hotwired/stimulus"

// Generic table-row selection mode (W5-6 / RES-110).
//
// Targets:
//   selectAll      — the thead "select all" checkbox (optional)
//   checkbox       — per-row checkbox (multiple)
//   toolbar        — the sticky bulk-actions toolbar (shown when >=1 row selected)
//   selectedCount  — span inside the toolbar that gets the live count
//
// Dispatches "selection:changed" on the controller element with
// detail = { count, ids } so per-page controllers (document-bulk-actions,
// supplier-merge, ...) can react to selection changes without re-implementing
// checkbox/toolbar wiring.
//
// Use the shared partial app/views/shared/_bulk_toolbar.html.erb to render
// the toolbar slot consistently.
export default class extends Controller {
  static targets = ["selectAll", "checkbox", "toolbar", "selectedCount"]

  connect() {
    this.updateUI()
    this.boundEscapeHandler = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.boundEscapeHandler)
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundEscapeHandler)
  }

  handleEscape(event) {
    if (event.key !== "Escape") return
    if (this.selectedIds.length === 0) return
    this.clearSelection()
  }

  toggleAll(event) {
    const checked = event.target.checked
    this.checkboxTargets.forEach((cb) => (cb.checked = checked))
    this.updateUI()
  }

  toggleCheckbox() {
    this.updateSelectAllState()
    this.updateUI()
  }

  clearSelection() {
    this.checkboxTargets.forEach((cb) => (cb.checked = false))
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    this.updateUI()
  }

  // ---- internals --------------------------------------------------------

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return
    const total = this.checkboxTargets.length
    const selected = this.selectedCheckboxes.length

    if (selected === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (selected === total) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  updateUI() {
    const count = this.selectedCheckboxes.length

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = count
    }

    if (this.hasToolbarTarget) {
      if (count > 0) {
        this.toolbarTarget.classList.remove("hidden")
        this.element.classList.add("pb-20")
      } else {
        this.toolbarTarget.classList.add("hidden")
        this.element.classList.remove("pb-20")
      }
    }

    this.dispatch("changed", { detail: { count, ids: this.selectedIds } })
  }

  get selectedCheckboxes() {
    return this.checkboxTargets.filter((cb) => cb.checked)
  }

  get selectedIds() {
    return this.selectedCheckboxes.map((cb) => cb.value)
  }
}
